// 2026-11-02: the chat survives a reload (James: "the history of my conversation is gone"). The transcript is kept in
// this browser per signed-in user (localStorage), the last MAX_LINES lines from the last MAX_AGE_MS, and restored when
// the page loads. Cards still "working" aren't restored (their job may have finished while the page was gone; the
// project screen shows the real state). Signing out clears it. A browser that won't store anything just starts empty.
import type { Line } from "./chat";

export const MAX_LINES = 60;
export const MAX_AGE_MS = 7 * 24 * 3600_000;
const PREFIX = "cao.chat.";
export const chatKey = (userId: string) => `${PREFIX}${userId}`;

/** What gets saved: plain lines and settled cards, newest MAX_LINES. */
export function toSave(lines: Line[]): Omit<Line, "id">[] {
  return lines
    .filter((l) => l.text || l.artifact)
    .slice(-MAX_LINES)
    .map(({ id: _id, ...rest }) => rest);
}

/** What comes back: well-formed lines, recent enough, not a card still "working". */
export function fromSaved(raw: unknown, now = Date.now()): Omit<Line, "id">[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((l): l is Omit<Line, "id"> => !!l && typeof l === "object" && ["you", "director", "studio"].includes((l as Line).who) && typeof (l as Line).text === "string" && typeof (l as Line).at === "number")
    .filter((l) => now - l.at < MAX_AGE_MS)
    .filter((l) => !l.artifact || (l.artifact as { status?: string }).status !== "working")
    .slice(-MAX_LINES);
}

type Store = Pick<Storage, "getItem" | "setItem" | "removeItem" | "key" | "length">;
const local = (): Store | null => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

export function loadChat(userId: string, s: Store | null = local()): Omit<Line, "id">[] {
  try {
    const v = s?.getItem(chatKey(userId));
    return v ? fromSaved(JSON.parse(v)) : [];
  } catch {
    return [];
  }
}

export function saveChat(userId: string, lines: Line[], s: Store | null = local()): void {
  try {
    const keep = toSave(lines);
    if (keep.length) s?.setItem(chatKey(userId), JSON.stringify(keep));
    else s?.removeItem(chatKey(userId));
  } catch {
    /* full or blocked: the chat just isn't kept */
  }
}

/** Signing out: no one else on this browser sees it. */
export function clearChats(s: Store | null = local()): void {
  try {
    if (!s) return;
    const keys: string[] = [];
    for (let i = 0; i < s.length; i++) {
      const k = s.key(i);
      if (k?.startsWith(PREFIX)) keys.push(k);
    }
    for (const k of keys) s.removeItem(k);
  } catch {
    /* nothing to clear */
  }
}
